import { useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Loader2, RotateCcwSquare } from "lucide-react";
import { Button } from "@/components/ui/button";
import { AlertDialog, AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { api, apiError } from "@/lib/api";

// Bulk "auto-orient" every template: the backend reads each image's real pixel
// dimensions and sets orientation = landscape when wider than tall, else portrait
// (square counts as portrait). Runs in cursor-paged batches so large catalogues
// never time out; we loop until the server reports done.
export const AutoOrientTemplatesButton = () => {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState({ processed: 0, updated: 0, landscape: 0, portrait: 0, skipped: 0 });
  const running = useRef(false);

  const run = async () => {
    if (running.current) return;
    running.current = true;
    setBusy(true);
    const totals = { processed: 0, updated: 0, landscape: 0, portrait: 0, skipped: 0 };
    let cursor = null;
    try {
      for (let i = 0; i < 1000; i++) {
        const { data } = await api.post("/admin/templates/auto-orient", null, {
          params: { ...(cursor ? { cursor } : {}), limit: 150 },
        });
        totals.processed += data.processed;
        totals.updated += data.updated;
        totals.landscape += data.landscape;
        totals.portrait += data.portrait;
        totals.skipped += data.skipped;
        setProgress({ ...totals });
        cursor = data.cursor;
        if (data.done) break;
      }
      qc.invalidateQueries({ queryKey: ["admin-templates"] });
      qc.invalidateQueries({ queryKey: ["home-featured"] });
      toast.success(`Auto-oriented ${totals.processed.toLocaleString()} templates`, {
        description: `${totals.updated.toLocaleString()} changed · ${totals.landscape.toLocaleString()} landscape · ${totals.portrait.toLocaleString()} portrait${totals.skipped ? ` · ${totals.skipped} skipped (no readable image)` : ""}`,
      });
      setOpen(false);
    } catch (e) {
      toast.error(apiError(e, "Auto-orient failed"), {
        description: totals.processed ? `Processed ${totals.processed} before stopping — you can run it again to continue.` : undefined,
      });
    } finally {
      running.current = false;
      setBusy(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={(o) => { if (!busy) setOpen(o); }}>
      <AlertDialogTrigger asChild>
        <Button data-testid="admin-auto-orient-button" variant="outline" className="border-violet-500/40 text-violet-200 hover:bg-violet-500/10">
          <RotateCcwSquare className="h-4 w-4 mr-1" /> Auto-Orient
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent data-testid="auto-orient-dialog" className="w-[calc(100%-2rem)] max-w-lg rounded-lg bg-[hsl(222_22%_8%)] border-white/10 text-white">
        <AlertDialogHeader className="text-left">
          <AlertDialogTitle data-testid="auto-orient-title" className="font-display text-lg">Auto-orient every template</AlertDialogTitle>
          <AlertDialogDescription data-testid="auto-orient-description" className="text-white/65">
            Each template's orientation will be set from its artwork: <strong className="text-white/80">landscape</strong> when the image is wider than tall, otherwise <strong className="text-white/80">portrait</strong> (square images are treated as portrait). You can still change any card's orientation by hand afterwards.
          </AlertDialogDescription>
        </AlertDialogHeader>

        {busy && (
          <div className="border-y border-white/10 py-4 space-y-2" data-testid="auto-orient-progress">
            <div className="flex items-center gap-2 text-violet-200 text-sm"><Loader2 className="h-4 w-4 animate-spin" /> Reading images…</div>
            <p className="text-sm text-white/70 font-mono" data-testid="auto-orient-progress-text">
              {progress.processed.toLocaleString()} processed · {progress.updated.toLocaleString()} changed
              <span className="text-white/40"> · {progress.landscape.toLocaleString()}▭ / {progress.portrait.toLocaleString()}▯</span>
            </p>
          </div>
        )}

        <AlertDialogFooter className="gap-2 sm:space-x-0">
          <Button type="button" data-testid="auto-orient-cancel-button" variant="outline" disabled={busy} onClick={() => setOpen(false)} className="border-white/15 bg-transparent text-white hover:bg-white/5">Cancel</Button>
          <Button type="button" data-testid="auto-orient-run-button" disabled={busy} onClick={run} className="bg-violet-500 text-white hover:bg-violet-400">
            {busy ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Orienting…</> : <><RotateCcwSquare className="h-4 w-4 mr-2" /> Auto-orient all</>}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
